/**
 * Weighted Volume (WVol = volume × close) — shared computation for the "Vol"
 * lower-pane indicator and the "AV_Band" overlay's spike detection.
 *
 * Port of the WVol pinescript with the app's HARDCODED parameters:
 *   • source smoothing = LinReg(7) of (volume × close)
 *   • fast EMA = 3, slow EMA = 21  (applied on the smoothed source)
 *   • spike when raw wVol > slow × 2.0
 *
 * Returns one point per input candle (times align to the candle array passed
 * in, so callers render against the same time grid).
 */
import { CandleRow } from '../components/charts/TradingChart';
import { linreg } from './candleTransforms';
import { emaSeries } from './jhm';

export const WVOL_LINREG = 7;
export const WVOL_FAST = 3;
export const WVOL_SLOW = 21;
export const WVOL_SPIKE_MULT = 2.0;

export interface WVolPoint {
  time: number;
  wVol: number;
  /** EMA(3) of LinReg(7)-smoothed wVol. NaN until seeded. */
  fast: number;
  /** EMA(21) of LinReg(7)-smoothed wVol. NaN until seeded. */
  slow: number;
  /** Raw wVol > slow × 2.0 (and slow is finite). */
  isSpike: boolean;
  /** close ≥ open on this bar. */
  isBull: boolean;
}

export function computeWVol(candles: CandleRow[]): WVolPoint[] {
  const n = candles.length;
  if (!n) return [];
  const wvolArr = candles.map(c => (Number(c.volume) || 0) * (Number(c.close) || 0));
  const src = linreg(wvolArr, WVOL_LINREG);
  const fast = emaSeries(src, WVOL_FAST);
  const slow = emaSeries(src, WVOL_SLOW);
  const out: WVolPoint[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const slowV = slow[i];
    const spikeLevel = Number.isFinite(slowV) ? slowV * WVOL_SPIKE_MULT : NaN;
    out[i] = {
      time: candles[i].time,
      wVol: wvolArr[i],
      fast: fast[i],
      slow: slow[i],
      isSpike: Number.isFinite(spikeLevel) && wvolArr[i] > spikeLevel,
      isBull: (Number(candles[i].close) || 0) >= (Number(candles[i].open) || 0),
    };
  }
  return out;
}
